import React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Select, MenuItem } from '@ha/dsl';
import { Language } from '@ha/intl';
import { useLocation } from 'react-router-dom';
import { GlobalState } from 'ha/types/store';

import { getCurrentLanguageCode, getCurrentPageLanguages } from '../selectors';
import { changeLanguage } from '../actions/changeLanguage';

export interface Props {
  className?: string;
  iconClassName?: string;
  kind?: 'compact' | 'full';
}

const LanguageSwitcher: React.FC<Props> = ({
  className,
  iconClassName,
  kind = 'compact',
}) => {
  const dispatch = useDispatch();

  const currentLanguage = useSelector(getCurrentLanguageCode);
  const location = useLocation();
  const languages =
    useSelector((state: GlobalState) =>
      getCurrentPageLanguages(state, location),
    ) || [];

  const handleChange = React.useCallback(
    e => {
      dispatch(changeLanguage(e.target.value as Language, location));
    },
    [dispatch, location],
  );

  const renderValue = (code: string) => {
    if (kind === 'compact') {
      return code.toUpperCase();
    }

    return (
      languages.find(lang => lang.code === code)?.localisedText ||
      code.toUpperCase()
    );
  };

  return (
    <div data-test-locator="Language switcher">
      <Select
        classes={
          iconClassName
            ? {
                icon: iconClassName,
              }
            : undefined
        }
        className={className}
        value={currentLanguage}
        onChange={handleChange}
        renderValue={renderValue as (code: unknown) => React.ReactNode}
        data-test-locator="Language switcher Selector"
        id="Language switcher"
      >
        {languages.map(({ code, localisedText }) => (
          <MenuItem
            key={code}
            value={code}
            data-test-locator={`Option ${code}`}
          >
            {localisedText}
          </MenuItem>
        ))}
      </Select>
    </div>
  );
};

export { LanguageSwitcher };
